import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import CriteriaParameterAutocomplete from '../graph/CriteriaParameterAutocomplete';

const CriteriaEditorPanel = ({
  open,
  isSmallScreen,
  editLabel,
  editDescription,
  editStatus,
  editIncludeCriteria,
  editExcludeCriteria,
  criteriaOperators,
  onClose,
  onSave,
  onChangeLabel,
  onChangeDescription,
  onChangeStatus,
  onAddCriterion,
  onUpdateCriterion,
  onDeleteCriterion,
}) => {
  const renderCriteriaList = (group, items) => (
    <Stack spacing={1.25}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {group === 'include' ? 'Include Criteria' : 'Exclude Criteria'}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => onAddCriterion(group)}
        >
          {group === 'include' ? 'Add Include' : 'Add Exclude'}
        </Button>
      </Stack>
      {items.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {group === 'include' ? 'No include criteria defined yet.' : 'No exclude criteria defined yet.'}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {items.map((criterion) => (
            <Stack
              key={criterion.id}
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <CriteriaParameterAutocomplete
                label="Field"
                value={criterion.field}
                onChange={(nextValue) => onUpdateCriterion(group, criterion.id, 'field', nextValue)}
              />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel id={`${group}-operator-${criterion.id}`}>Operator</InputLabel>
                <Select
                  labelId={`${group}-operator-${criterion.id}`}
                  value={criterion.operator}
                  label="Operator"
                  onChange={(e) => onUpdateCriterion(group, criterion.id, 'operator', e.target.value)}
                >
                  {criteriaOperators.map((operator) => (
                    <MenuItem key={operator.value} value={operator.value}>
                      {operator.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Value"
                size="small"
                value={criterion.value}
                onChange={(e) => onUpdateCriterion(group, criterion.id, 'value', e.target.value)}
                fullWidth
              />
              <IconButton
                size="small"
                color="error"
                aria-label={`delete ${group} criterion`}
                onClick={() => onDeleteCriterion(group, criterion.id)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
    >
      <DialogTitle>Edit Cohort Node</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Label"
            value={editLabel}
            onChange={(e) => onChangeLabel(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
          <TextField
            label="Description"
            value={editDescription}
            onChange={(e) => onChangeDescription(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="Short explanation of this cohort node"
          />
          <FormControl fullWidth size="small">
            <InputLabel id="cohort-node-status-label">Status</InputLabel>
            <Select
              labelId="cohort-node-status-label"
              value={editStatus}
              label="Status"
              onChange={(e) => onChangeStatus(e.target.value)}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="unedited">Unedited</MenuItem>
              <MenuItem value="edited">Edited</MenuItem>
              <MenuItem value="executing">Executing</MenuItem>
              <MenuItem value="executed">Executed</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </Select>
          </FormControl>

          {renderCriteriaList('include', editIncludeCriteria)}
          {renderCriteriaList('exclude', editExcludeCriteria)}

          <Typography variant="caption" color="text.secondary">
            Saving updates node label, description, status, and include/exclude criteria.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={!editLabel.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CriteriaEditorPanel;

